import { apiConfig } from '@nomiss/configs';
import { loadAdminSession } from './admin-session.service';
import {
  buildApiHeaders,
  fetchWithAuthRecovery,
  resolveAccessToken,
} from './sdk-client';
import { ApiRequestError } from './api-request.util';

export class AdminAlertsError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminAlertsError');
  }
}

export type AdminAlertEmailScope = 'off' | 'all' | 'ingestion' | 'scraping';

export type AdminAlertConfig = {
  ingestionFailureThreshold: number;
  scraperFailureThreshold: number;
  dlqThreshold: number;
  cooldownMinutes: number;
  recipientEmails: string[];
  notificationChannels: string[];
  emailScope: AdminAlertEmailScope;
  emailSubject: string;
  emailIntro: string;
  emailFooter: string;
};

export type PatchAdminAlertConfigInput = Partial<AdminAlertConfig>;

export type AdminAlertTypeResult = {
  shouldFire: boolean;
  sent: boolean;
  skippedReason: string | null;
  cooldownActive: boolean;
  cooldownSecondsRemaining: number | null;
  reasons: string[];
  details: string[];
  counts: Record<string, number>;
};

export type AdminAlertEvaluation = {
  evaluatedAt: string;
  skipAllReason: string | null;
  ingestion: AdminAlertTypeResult;
  scraping: AdminAlertTypeResult;
};

export type AdminAlertHistoryItem = {
  id: string;
  sentAt: string;
  alertType: 'ingestion' | 'scraping';
  source: 'run_complete' | 'sweep';
  title: string;
  status: 'sent' | 'failed';
  errorMessage: string | null;
  recipientEmails: string[];
  emailSubject: string;
  emailIntro: string;
  emailFooter: string;
  reasons: string[];
  details: string[];
};

export type AdminAlertHistoryMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type AdminAlertHistory = {
  items: AdminAlertHistoryItem[];
  meta: AdminAlertHistoryMeta;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string');
}

function asNumberRecord(value: unknown): Record<string, number> {
  if (!isRecord(value)) {
    return {};
  }
  const result: Record<string, number> = {};
  for (const [key, entry] of Object.entries(value)) {
    result[key] = asNumber(entry, 0);
  }
  return result;
}

function parseErrorMessage(json: unknown, fallback: string): string {
  if (!isRecord(json)) {
    return fallback;
  }
  const rawMessage = json.message;
  if (Array.isArray(rawMessage)) {
    return rawMessage.join(', ');
  }
  if (typeof rawMessage === 'string') {
    return rawMessage;
  }
  return fallback;
}

function parseEmailScope(value: unknown): AdminAlertEmailScope {
  if (
    value === 'off' ||
    value === 'all' ||
    value === 'ingestion' ||
    value === 'scraping'
  ) {
    return value;
  }
  return 'all';
}

function parseConfig(value: unknown): AdminAlertConfig | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    ingestionFailureThreshold: asNumber(value.ingestionFailureThreshold, 1),
    scraperFailureThreshold: asNumber(value.scraperFailureThreshold, 5),
    dlqThreshold: asNumber(value.dlqThreshold, 1),
    cooldownMinutes: asNumber(value.cooldownMinutes, 60),
    recipientEmails: asStringArray(value.recipientEmails),
    notificationChannels: asStringArray(value.notificationChannels),
    emailScope: parseEmailScope(value.emailScope),
    emailSubject: asString(
      value.emailSubject,
      'NOMISS operational alert: {{alertType}}',
    ),
    emailIntro: asString(
      value.emailIntro,
      'An operational {{alertType}} issue was detected.',
    ),
    emailFooter: asString(
      value.emailFooter,
      'Review Alerts and data-sync queues in the admin panel. Run-complete emails list every failed job from the last finished wave. Leftover-signal emails (YouTube, DLQs, stale sports) are sent at most once per cooldown window.',
    ),
  };
}

function parseTypeResult(value: unknown): AdminAlertTypeResult {
  const row = isRecord(value) ? value : {};
  return {
    shouldFire: row.shouldFire === true,
    sent: row.sent === true,
    skippedReason:
      typeof row.skippedReason === 'string' ? row.skippedReason : null,
    cooldownActive: row.cooldownActive === true,
    cooldownSecondsRemaining:
      typeof row.cooldownSecondsRemaining === 'number'
        ? row.cooldownSecondsRemaining
        : null,
    reasons: asStringArray(row.reasons),
    details: asStringArray(row.details),
    counts: asNumberRecord(row.counts),
  };
}

function parseAlertType(value: unknown): 'ingestion' | 'scraping' {
  return value === 'scraping' ? 'scraping' : 'ingestion';
}

function parseHistorySource(value: unknown): 'run_complete' | 'sweep' {
  return value === 'sweep' ? 'sweep' : 'run_complete';
}

function parseHistoryStatus(value: unknown): 'sent' | 'failed' {
  return value === 'failed' ? 'failed' : 'sent';
}

function parseHistoryItem(value: unknown): AdminAlertHistoryItem | null {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }
  return {
    id: value.id,
    sentAt: asString(value.sentAt),
    alertType: parseAlertType(value.alertType),
    source: parseHistorySource(value.source),
    title: asString(value.title),
    status: parseHistoryStatus(value.status),
    errorMessage:
      typeof value.errorMessage === 'string' ? value.errorMessage : null,
    recipientEmails: asStringArray(value.recipientEmails),
    emailSubject: asString(value.emailSubject),
    emailIntro: asString(value.emailIntro),
    emailFooter: asString(value.emailFooter),
    reasons: asStringArray(value.reasons),
    details: asStringArray(value.details),
  };
}

function parseHistoryMeta(
  value: unknown,
  itemCount: number,
): AdminAlertHistoryMeta {
  if (!isRecord(value)) {
    return {
      page: 1,
      limit: itemCount,
      total: itemCount,
      totalPages: itemCount === 0 ? 0 : 1,
    };
  }
  const page = Math.max(1, asNumber(value.page, 1));
  const limit = Math.max(1, asNumber(value.limit, 20));
  const total = Math.max(0, asNumber(value.total, itemCount));
  const totalPages = Math.max(
    0,
    asNumber(value.totalPages, total === 0 ? 0 : Math.ceil(total / limit)),
  );
  return { page, limit, total, totalPages };
}

function parseHistory(value: unknown): AdminAlertHistory {
  if (!isRecord(value)) {
    return { items: [], meta: parseHistoryMeta(undefined, 0) };
  }
  const rawItems = Array.isArray(value.items) ? value.items : [];
  const items = rawItems
    .map((item) => parseHistoryItem(item))
    .filter((item): item is AdminAlertHistoryItem => item !== null);
  return {
    items,
    meta: parseHistoryMeta(value.meta, items.length),
  };
}

function parseEvaluation(value: unknown): AdminAlertEvaluation | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    evaluatedAt: asString(value.evaluatedAt),
    skipAllReason:
      typeof value.skipAllReason === 'string' ? value.skipAllReason : null,
    ingestion: parseTypeResult(value.ingestion),
    scraping: parseTypeResult(value.scraping),
  };
}

function alertsApiHeaders(
  init?: Record<string, string>,
): Record<string, string> {
  const token = loadAdminSession()?.accessToken ?? resolveAccessToken();
  if (!token) {
    throw new AdminAlertsError(401, 'Not signed in');
  }
  return buildApiHeaders({
    ...init,
    Authorization: `Bearer ${token}`,
  });
}

async function readEnvelopeData(
  response: Response,
  fallback: string,
): Promise<unknown> {
  const json = (await response.json().catch(() => ({}))) as unknown;
  if (!response.ok) {
    throw new AdminAlertsError(
      response.status,
      parseErrorMessage(json, fallback),
    );
  }
  const envelope = isRecord(json) ? json : {};
  return envelope.data;
}

export async function getAdminAlertConfig(): Promise<AdminAlertConfig> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/alerts/config`,
    {
      headers: alertsApiHeaders(),
    },
  );
  const data = await readEnvelopeData(
    response,
    'Failed to load alert configuration',
  );
  return (
    parseConfig(data) ?? {
      ingestionFailureThreshold: 1,
      scraperFailureThreshold: 5,
      dlqThreshold: 1,
      cooldownMinutes: 60,
      recipientEmails: [],
      notificationChannels: ['email'],
      emailScope: 'all',
      emailSubject: 'NOMISS operational alert: {{alertType}}',
      emailIntro: 'An operational {{alertType}} issue was detected.',
      emailFooter:
        'Review Alerts and data-sync queues in the admin panel. Run-complete emails list every failed job from the last finished wave. Leftover-signal emails (YouTube, DLQs, stale sports) are sent at most once per cooldown window.',
    }
  );
}

export async function updateAdminAlertConfig(
  input: PatchAdminAlertConfigInput,
): Promise<AdminAlertConfig> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/alerts/config`,
    {
      method: 'PATCH',
      headers: alertsApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(input),
    },
  );
  const data = await readEnvelopeData(
    response,
    'Failed to update alert configuration',
  );
  const parsed = parseConfig(data);
  if (parsed === null) {
    throw new AdminAlertsError(500, 'Invalid alert configuration response');
  }
  return parsed;
}

export async function getAdminAlertHistory(params?: {
  page?: number;
  limit?: number;
}): Promise<AdminAlertHistory> {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/alerts/history?${query.toString()}`,
    {
      headers: alertsApiHeaders(),
    },
  );
  const data = await readEnvelopeData(response, 'Failed to load alert history');
  return parseHistory(data);
}

export async function getAdminAlertStatus(): Promise<AdminAlertEvaluation | null> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/alerts/status`,
    {
      headers: alertsApiHeaders(),
    },
  );
  const data = await readEnvelopeData(response, 'Failed to load alert status');
  if (data === null) {
    return null;
  }
  return parseEvaluation(data);
}

export type ListingUrlAlertReason =
  | 'redirected'
  | 'http_error'
  | 'navigation_failed'
  | 'empty_listing';

export type ListingUrlAlertStatus = 'open' | 'false_positive';

export type ListingUrlAlertItem = {
  id: string;
  leagueListingUrlId: string;
  leagueCode: string;
  leagueName: string;
  platformCode: string;
  platformName: string;
  listingUrl: string;
  reason: ListingUrlAlertReason;
  signal: string;
  status: ListingUrlAlertStatus;
  detail: string | null;
  httpStatus: number | null;
  finalUrl: string | null;
  openedAt: string;
  lastSeenAt: string;
  notifiedAt: string | null;
};

function parseListingUrlAlertReason(value: unknown): ListingUrlAlertReason {
  if (
    value === 'redirected' ||
    value === 'http_error' ||
    value === 'navigation_failed' ||
    value === 'empty_listing'
  ) {
    return value;
  }
  return 'empty_listing';
}

function parseListingUrlAlertItem(value: unknown): ListingUrlAlertItem | null {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }
  return {
    id: value.id,
    leagueListingUrlId: asString(value.leagueListingUrlId),
    leagueCode: asString(value.leagueCode),
    leagueName: asString(value.leagueName),
    platformCode: asString(value.platformCode),
    platformName: asString(value.platformName),
    listingUrl: asString(value.listingUrl),
    reason: parseListingUrlAlertReason(value.reason),
    signal: asString(value.signal),
    status: value.status === 'false_positive' ? 'false_positive' : 'open',
    detail: typeof value.detail === 'string' ? value.detail : null,
    httpStatus: typeof value.httpStatus === 'number' ? value.httpStatus : null,
    finalUrl: typeof value.finalUrl === 'string' ? value.finalUrl : null,
    openedAt: asString(value.openedAt),
    lastSeenAt: asString(value.lastSeenAt),
    notifiedAt: typeof value.notifiedAt === 'string' ? value.notifiedAt : null,
  };
}

export function parseListingUrlAlerts(value: unknown): ListingUrlAlertItem[] {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    return [];
  }
  return value.items.flatMap((item) => {
    const parsed = parseListingUrlAlertItem(item);
    return parsed === null ? [] : [parsed];
  });
}

export async function getAdminListingUrlAlerts(params?: {
  status?: ListingUrlAlertStatus;
}): Promise<ListingUrlAlertItem[]> {
  const status = params?.status ?? 'open';
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/alerts/listing-urls?status=${status}`,
    {
      headers: alertsApiHeaders(),
    },
  );
  const data = await readEnvelopeData(response, 'Failed to load listing URL alerts');
  return parseListingUrlAlerts(data);
}

export async function markListingUrlAlertFalsePositive(
  id: string,
): Promise<ListingUrlAlertItem> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/alerts/listing-urls/${id}/false-positive`,
    {
      method: 'POST',
      headers: alertsApiHeaders(),
    },
  );
  const data = await readEnvelopeData(
    response,
    'Failed to mark the listing URL alert as a false positive',
  );
  const parsed = parseListingUrlAlertItem(data);
  if (parsed === null) {
    throw new AdminAlertsError(500, 'Invalid listing URL alert response');
  }
  return parsed;
}

export async function clearListingUrlAlertSuppression(
  id: string,
): Promise<void> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/alerts/listing-urls/${id}/clear-suppression`,
    {
      method: 'POST',
      headers: alertsApiHeaders(),
    },
  );
  await readEnvelopeData(response, 'Failed to clear the listing URL suppression');
}

export async function evaluateAdminAlerts(options?: {
  dryRun?: boolean;
}): Promise<AdminAlertEvaluation> {
  const dryRun = options?.dryRun !== false;
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/alerts/evaluate?dryRun=${dryRun ? 'true' : 'false'}`,
    {
      method: 'POST',
      headers: alertsApiHeaders(),
    },
  );
  const data = await readEnvelopeData(response, 'Failed to evaluate alerts');
  const parsed = parseEvaluation(data);
  if (parsed === null) {
    throw new AdminAlertsError(500, 'Invalid alert evaluation response');
  }
  return parsed;
}
